import { z } from 'zod';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  naturezaMovimentoSchema,
  paginacaoSchema,
  respostaListaSchema,
  tipoMovimentoContaSchema,
  type TipoMovimentoConta,
  uuidSchema,
} from './comum.ts';
import { ROTULO_GRUPO_CONTA_CONTABIL, type GrupoContaContabil } from './financeiro.ts';

/**
 * Movimento de caixa (`.spec/010`, módulo Financeiro; `.spec/016`, seção 10).
 *
 * Dois recursos, e a diferença entre eles é a razão do módulo existir:
 *
 * - **Lançamento** é o documento que o usuário digita. Tem CRUD.
 * - **Movimentação** é o livro-razão do caixa. É **somente leitura** pela API:
 *   ninguém insere linha nele à mão. Quem escreve é o service, junto com o
 *   documento que a originou, na mesma transação — é isso que garante que o
 *   saldo da conta corrente reflita o extrato bancário.
 *
 * Não existe `criarMovimentacaoSchema` de propósito. Uma rota de escrita no
 * livro-razão permitiria uma movimentação sem documento e sem efeito no saldo,
 * e o job `recalcula-saldos` passaria a acusar divergência todo dia sem que
 * ninguém soubesse de onde ela veio.
 */

/* -------------------------------------------------------------------------- */
/* Lançamento em conta corrente                                               */
/* -------------------------------------------------------------------------- */

/**
 * Grupos do plano de contas que cada sentido aceita.
 *
 * Crédito é entrada de dinheiro, então classifica em **receita**. Débito é
 * saída, e sai em **custo de obra** ou **despesa** — a distinção entre os dois
 * é o que separa o que vai para o DRE da obra do que é despesa da empresa.
 *
 * Ativo e passivo ficam de fora dos dois: movimentação entre contas de balanço
 * (transferência entre bancos, quitação de empréstimo) não é lançamento manual
 * de caixa com contrapartida de resultado, e oferecê-las aqui produziria
 * classificação errada no balancete sem nada recusar.
 *
 * Os dois conjuntos são **disjuntos**: nenhuma conta serve aos dois sentidos.
 * É por isso que a tela limpa a conta escolhida quando o sentido muda — a
 * anterior nunca continua válida.
 *
 * Fica no contrato, e não na API nem no formulário, porque os dois precisam da
 * **mesma** regra: o combobox esconde o que a API recusaria, e a API recusa o
 * que um `curl` mandaria de qualquer jeito. Duas cópias divergiriam na primeira
 * correção.
 */
export const GRUPOS_POR_TIPO_MOVIMENTO: Readonly<
  Record<TipoMovimentoConta, readonly GrupoContaContabil[]>
> = {
  CREDITO: ['RECEITA'],
  DEBITO: ['CUSTO', 'DESPESA'],
};

export function grupoAceitoNoLancamento(
  tipo: TipoMovimentoConta,
  grupo: GrupoContaContabil,
): boolean {
  return GRUPOS_POR_TIPO_MOVIMENTO[tipo].includes(grupo);
}

/** Os grupos permitidos em texto corrido, para mensagem de erro e dica de tela. */
export function descreverGruposDoLancamento(tipo: TipoMovimentoConta): string {
  const nomes = GRUPOS_POR_TIPO_MOVIMENTO[tipo].map((g) => ROTULO_GRUPO_CONTA_CONTABIL[g]);
  return nomes.length === 1 ? (nomes[0] as string) : nomes.join(' ou ');
}

/**
 * Valor do lançamento: **sempre positivo**.
 *
 * O sentido do dinheiro é o `tipo` (crédito ou débito), nunca o sinal do
 * número. Aceitar negativo criaria duas formas de dizer a mesma coisa — um
 * "crédito de −100" e um "débito de 100" —, e a soma que forma o saldo teria de
 * decidir qual das duas obedecer.
 */
const valorDoLancamentoSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor do lançamento deve ser maior que zero.',
);

export const criarLancamentoContaCorrenteSchema = z.object({
  /** Empresa do grupo — `pessoas` com `is_empresa`. */
  empresaId: uuidSchema,
  /** Dia do movimento. Data de negócio, sem hora: o lançamento é do dia 10. */
  data: dataDoFatoSchema,
  /** `CREDITO` soma ao saldo da conta corrente; `DEBITO` subtrai. */
  tipo: tipoMovimentoContaSchema,
  /** Natureza do movimento: pagamento, recebimento, tarifa, transferência. */
  historicoBancarioId: uuidSchema,
  /**
   * Forma de pagamento: dinheiro, pix, cheque.
   *
   * `.spec/010` não lista este campo no lançamento — **decisão deste módulo**.
   * A movimentação gerada exige a forma de pagamento (`.spec/016`, seção 10), e
   * as alternativas eram afrouxar a coluna do livro-razão para todas as origens
   * ou o service escolher um histórico por conta própria. Pedir o dado a quem
   * está com o documento na mão é mais honesto que as duas.
   */
  historicoFinanceiroId: uuidSchema,
  /** Conta corrente cujo saldo o lançamento altera. */
  contaCorrenteId: uuidSchema,
  /** Classificação no plano de contas. */
  contaContabilId: uuidSchema,
  valor: valorDoLancamentoSchema,
  observacao: z.string().trim().max(500).prefault(''),
});

export type CriarLancamentoContaCorrente = z.infer<typeof criarLancamentoContaCorrenteSchema>;
export const atualizarLancamentoContaCorrenteSchema = criarLancamentoContaCorrenteSchema;
export type AtualizarLancamentoContaCorrente = z.infer<
  typeof atualizarLancamentoContaCorrenteSchema
>;

/**
 * Lançamento como a API o devolve.
 *
 * Os nomes de empresa, conta e históricos vêm **desnormalizados**: a listagem
 * mostra "Banco do Brasil 1234-5" e não um uuid, e resolver isso no cliente
 * significaria uma consulta por linha — 25 idas ao servidor para desenhar uma
 * página.
 */
export const lancamentoContaCorrenteSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  data: dataSchema,
  tipo: tipoMovimentoContaSchema,
  historicoBancarioId: uuidSchema,
  historicoBancarioDescricao: z.string().nullable(),
  historicoFinanceiroId: uuidSchema,
  historicoFinanceiroDescricao: z.string().nullable(),
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string().nullable(),
  contaContabilId: uuidSchema,
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  /** String decimal, nunca number: ponto flutuante perde centavos em silêncio. */
  valor: dinheiroSchema,
  observacao: z.string(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type LancamentoContaCorrente = z.infer<typeof lancamentoContaCorrenteSchema>;

export const listaLancamentosContaCorrenteSchema = respostaListaSchema(
  lancamentoContaCorrenteSchema,
);
export type ListaLancamentosContaCorrente = z.infer<typeof listaLancamentosContaCorrenteSchema>;

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: lançamento é documento de movimento, não cadastro — ele existe
 * ou não existe. O recorte natural é **conta e período**, que é como se procura
 * um lançamento ("o que saiu do Banco do Brasil em julho"), e é exatamente o
 * índice que a tabela tem.
 */
export const listarLancamentosContaCorrenteQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  contaCorrenteId: uuidSchema.optional(),
  tipo: tipoMovimentoContaSchema.optional(),
  historicoBancarioId: uuidSchema.optional(),
  contaContabilId: uuidSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarLancamentosContaCorrenteQuery = z.infer<
  typeof listarLancamentosContaCorrenteQuerySchema
>;

export const COLUNAS_ORDENAVEIS_LANCAMENTO_CONTA_CORRENTE = ['data', 'valor', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Movimentação em conta corrente — o extrato                                 */
/* -------------------------------------------------------------------------- */

/**
 * Origem da movimentação, já resolvida para a tela.
 *
 * O extrato precisa dizer de onde veio cada linha, e as quatro colunas de origem
 * da tabela são mutuamente exclusivas por `CHECK`. Devolver o código em vez de
 * quatro campos nulos evita que o cliente reimplemente essa exclusividade.
 *
 * `TRANSFERENCIA` é a **quarta** origem, e a única que aparece **duas vezes com
 * o mesmo id**: a transferência entre contas correntes gera o débito na origem e
 * o crédito no destino, e as duas linhas apontam para o mesmo documento. É o
 * espelho do que a transferência de estoque fez em `movimentacoes_estoque`, e é
 * por isso que o índice único dela inclui o `tipo`.
 */
export const ORIGENS_MOVIMENTACAO = [
  'LANCAMENTO',
  'BAIXA_PAGAR',
  'BAIXA_RECEBER',
  'TRANSFERENCIA',
] as const;
export const origemMovimentacaoSchema = z.enum(ORIGENS_MOVIMENTACAO);
export type OrigemMovimentacao = z.infer<typeof origemMovimentacaoSchema>;

export const ROTULO_ORIGEM_MOVIMENTACAO: Readonly<Record<OrigemMovimentacao, string>> = {
  LANCAMENTO: 'Lançamento manual',
  BAIXA_PAGAR: 'Baixa de conta a pagar',
  BAIXA_RECEBER: 'Baixa de conta a receber',
  TRANSFERENCIA: 'Transferência entre contas',
};

export const ROTULO_TIPO_MOVIMENTO: Readonly<
  Record<z.infer<typeof tipoMovimentoContaSchema>, string>
> = {
  CREDITO: 'Crédito',
  DEBITO: 'Débito',
};

export const ROTULO_NATUREZA_MOVIMENTO: Readonly<
  Record<z.infer<typeof naturezaMovimentoSchema>, string>
> = {
  PRINCIPAL: 'Principal',
  JUROS: 'Juros',
  OUTROS: 'Outros',
  DESCONTO: 'Desconto',
};

export const movimentacaoContaCorrenteSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  data: dataSchema,
  tipo: tipoMovimentoContaSchema,
  natureza: naturezaMovimentoSchema,
  historicoBancarioDescricao: z.string().nullable(),
  historicoFinanceiroDescricao: z.string().nullable(),
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string().nullable(),
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  origem: origemMovimentacaoSchema,
  /** Id do documento de origem — o lançamento, hoje; a baixa, quando existir. */
  origemId: uuidSchema,
  valor: dinheiroSchema,
  observacao: z.string(),
  createdAt: dataHoraSchema,
});

export type MovimentacaoContaCorrente = z.infer<typeof movimentacaoContaCorrenteSchema>;

/**
 * Extrato: a página de movimentações **mais os totais do período inteiro**.
 *
 * Os totais não saem da soma da página — somariam 25 linhas de um período com
 * 400 — e por isso não podem ser calculados no cliente. Vêm do mesmo `WHERE` da
 * listagem, numa segunda consulta agregada.
 */
export const extratoContaCorrenteSchema = respostaListaSchema(
  movimentacaoContaCorrenteSchema,
).extend({
  totalCreditos: dinheiroSchema,
  totalDebitos: dinheiroSchema,
  /** `totalCreditos − totalDebitos`. O que o período movimentou, com sinal. */
  saldoDoPeriodo: dinheiroSchema,
  /**
   * Saldo materializado da conta filtrada, para conferência lado a lado.
   *
   * Nulo quando a consulta não filtra uma conta: somar o saldo de contas de
   * empresas diferentes não significa nada.
   */
  saldoAtualDaConta: dinheiroSchema.nullable(),
});

export type ExtratoContaCorrente = z.infer<typeof extratoContaCorrenteSchema>;

export const listarMovimentacoesQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  contaCorrenteId: uuidSchema.optional(),
  tipo: tipoMovimentoContaSchema.optional(),
  natureza: naturezaMovimentoSchema.optional(),
  origem: origemMovimentacaoSchema.optional(),
  de: dataSchema.optional(),
  ate: dataSchema.optional(),
});

export type ListarMovimentacoesQuery = z.infer<typeof listarMovimentacoesQuerySchema>;

export const COLUNAS_ORDENAVEIS_MOVIMENTACAO = ['data', 'valor', 'createdAt'] as const;
